/**
 * Schichttausch (G-41): Zwei Mitarbeiter tauschen am selben Tag ihre Schicht
 * (POST /shift-plan/swap; atomar, nur die Schicht wird getauscht).
 *
 * Einstieg: aus dem RebookSheet („Tauschen …") mit einer fest vorgegebenen
 * Ausgangs-Planzeile. Der Tauschpartner wird aus /shift-plan/swap-candidates
 * gewählt (selber Tag, andere Schicht, anderer Mitarbeiter, sichtbar).
 * Guards (Abwesenheit → 409 usw.) meldet das Backend mit deutschem Text.
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeftRight } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { fmtDate } from "@/lib/format"
import { api, apiError } from "@/services/api"
import type { ShiftPlanEntry } from "@/types"

interface SwapShiftDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Ausgangs-Planzeile (Seite A) — fest vorgegeben aus dem Planraster */
  entry: ShiftPlanEntry | null
}

export function SwapShiftDialog({ open, onOpenChange, entry }: SwapShiftDialogProps) {
  const queryClient = useQueryClient()
  const [partnerId, setPartnerId] = useState("")

  useEffect(() => {
    if (open) setPartnerId("")
  }, [open])

  const candidates = useQuery({
    queryKey: ["swapCandidates", entry?.id],
    queryFn: () => api.planning.swapCandidates(entry!.id),
    enabled: open && entry !== null,
  })

  const partner = (candidates.data ?? []).find((c) => c.id === partnerId) ?? null

  const swap = useMutation({
    mutationFn: () =>
      api.planning.swapShifts({
        entryAId: Number(entry!.id),
        entryBId: Number(partnerId),
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      queryClient.invalidateQueries({ queryKey: ["attendance"] })
      toast.success(
        `Schicht getauscht: ${res.entryA.employeeName} (${res.entryA.shiftName}) ↔ ` +
          `${res.entryB.employeeName} (${res.entryB.shiftName})`
      )
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Schicht tauschen</DialogTitle>
          <DialogDescription>
            Zwei Mitarbeiter tauschen an diesem Tag ihre Schicht (G-41); der Tausch ist
            atomar, es wechselt nur die Schicht. Abwesende werden vom Backend geschützt.
          </DialogDescription>
        </DialogHeader>

        {entry && (
          <div className="grid gap-4 py-2">
            {/* Ausgangs-Planzeile (Seite A) */}
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">{entry.employeeName}</p>
              <p className="text-muted-foreground">
                {fmtDate(entry.date)} · {entry.shiftName} / {entry.positionName}
                {entry.workplaceName && ` · ${entry.workplaceName}`}
              </p>
            </div>

            {/* Tauschpartner (Seite B) */}
            <div className="space-y-1.5">
              <Label>Tauschpartner (selber Tag, andere Schicht)</Label>
              {candidates.isLoading ? (
                <p className="text-sm text-muted-foreground">Wird geladen …</p>
              ) : (candidates.data ?? []).length === 0 ? (
                <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  Keine tauschbaren Planzeilen an diesem Tag (andere Schicht, anwesend).
                </p>
              ) : (
                <Select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                  <option value="">— Partner wählen —</option>
                  {(candidates.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.employeeName} — {c.positionName} — {c.shiftName}
                    </option>
                  ))}
                </Select>
              )}
            </div>

            {/* Vorschau */}
            {partner && (
              <div className="flex items-center gap-2 rounded-md border bg-card p-3 text-sm">
                <ArrowLeftRight className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-muted-foreground">Nach dem Tausch:</p>
                  <p>
                    <span className="font-medium">{entry.employeeName}</span> → {partner.shiftName}
                  </p>
                  <p>
                    <span className="font-medium">{partner.employeeName}</span> → {entry.shiftName}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!entry || !partner || swap.isPending} onClick={() => swap.mutate()}>
            Tauschen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
